import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { XIcon, SparklesIcon, BotIcon, UserIcon, MapPinIcon, WalletIcon, ClockIcon, CarIcon, CheckCircle2Icon, TrendingUpIcon, LightbulbIcon, ArrowDownIcon, TargetIcon, PhoneIcon, MailIcon, MessageCircleIcon, FileTextIcon, ListIcon, StickyNoteIcon, ShieldCheckIcon, ShieldAlertIcon, PlusIcon, CheckIcon, ChevronDownIcon } from 'lucide-react';
export const PIPELINE_STAGES = ['Discovered', 'Qualified', 'Interested', 'Negotiating', 'Ready To Buy', 'Won', 'Lost'] as const;
export type LeadStatus = (typeof PIPELINE_STAGES)[number];
export const OWNERS = ['Unassigned', 'Fadi', 'Sara', 'Sales Team'] as const;
export interface Lead {
  name: string;
  intentScore: number;
  lookingFor: string;
  budget: string;
  timeline: string;
  location: string;
  matchedItem: string;
  status: LeadStatus;
  summary: string;
  // Contact / CRM (only present once customer has engaged)
  phone?: string;
  email?: string;
  preferredContact?: string;
  preferredLanguage?: string;
  source?: string;
  lastActive?: string;
  receivedDate?: string;
  owner?: string;
  nextAction?: string;
  nextActionDue?: string;
  consent?: 'shared' | 'requested' | 'not_shared';
}
interface LeadDetailModalProps {
  lead: Lead;
  onClose: () => void;
  onStatusChange?: (status: LeadStatus) => void;
  onOwnerChange?: (owner: string) => void;
}
type DetailTab = 'Overview' | 'Conversation Timeline' | 'Customer Details' | 'Matched Listings' | 'AI Recommendations' | 'Activity Log' | 'Notes';
function intentBucket(score: number) {
  if (score >= 90) return {
    label: 'Ready To Buy',
    color: 'text-green-700',
    bg: 'bg-green-100',
    bar: 'bg-green-500'
  };
  if (score >= 75) return {
    label: 'High Intent',
    color: 'text-primary-indigo',
    bg: 'bg-indigo-100',
    bar: 'bg-primary-indigo'
  };
  if (score >= 50) return {
    label: 'Interested',
    color: 'text-amber-600',
    bg: 'bg-amber-100',
    bar: 'bg-amber-500'
  };
  return {
    label: 'Early Stage',
    color: 'text-slate-500',
    bg: 'bg-slate-100',
    bar: 'bg-slate-400'
  };
}
export function LeadDetailModal({
  lead,
  onClose,
  onStatusChange,
  onOwnerChange
}: LeadDetailModalProps) {
  const bucket = intentBucket(lead.intentScore);
  const [tab, setTab] = useState<DetailTab>('Overview');
  const [status, setStatus] = useState<LeadStatus>(lead.status);
  const [owner, setOwner] = useState<string>(lead.owner ?? 'Unassigned');
  const [notes, setNotes] = useState('Customer prefers white GCC vehicles only. Do not suggest imported vehicles.');
  const [activity, setActivity] = useState<{
    text: string;
    time: string;
  }[]>([{
    text: 'Lead created from Talab AI conversation',
    time: '2 days ago'
  }, {
    text: 'Customer asked about delivery options',
    time: '2 days ago'
  }, {
    text: 'Customer requested seller contact',
    time: '1 day ago'
  }, {
    text: `Owner set to ${lead.owner ?? 'Sara'}`,
    time: '1 day ago'
  }]);
  const hasContact = lead.consent === 'shared';
  const changeStatus = (next: LeadStatus) => {
    if (next === status) return;
    setActivity(a => [{
      text: `Status changed from ${status} to ${next} by Fadi`,
      time: 'Just now'
    }, ...a]);
    setStatus(next);
    onStatusChange?.(next);
  };
  const changeOwner = (next: string) => {
    if (next === owner) return;
    setActivity(a => [{
      text: `Owner changed from ${owner} to ${next} by Fadi`,
      time: 'Just now'
    }, ...a]);
    setOwner(next);
    onOwnerChange?.(next);
  };
  const tabs: {
    id: DetailTab;
    icon: React.ReactNode;
  }[] = [{
    id: 'Overview',
    icon: <FileTextIcon className="w-4 h-4" data-id="element-1657" />
  }, {
    id: 'Conversation Timeline',
    icon: <MessageCircleIcon className="w-4 h-4" data-id="element-1658" />
  }, {
    id: 'Customer Details',
    icon: <UserIcon className="w-4 h-4" data-id="element-1659" />
  }, {
    id: 'Matched Listings',
    icon: <CarIcon className="w-4 h-4" data-id="element-1660" />
  }, {
    id: 'AI Recommendations',
    icon: <SparklesIcon className="w-4 h-4" data-id="element-1661" />
  }, {
    id: 'Activity Log',
    icon: <ListIcon className="w-4 h-4" data-id="element-1662" />
  }, {
    id: 'Notes',
    icon: <StickyNoteIcon className="w-4 h-4" data-id="element-1663" />
  }];
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6" data-id="element-1664">
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={onClose} data-id="element-1665" />
      <motion.div initial={{
      opacity: 0,
      scale: 0.96,
      y: 20
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} exit={{
      opacity: 0,
      scale: 0.96,
      y: 20
    }} className="relative w-full max-w-6xl bg-[#F8FAFC] rounded-2xl shadow-2xl overflow-hidden flex flex-col h-[88vh]" data-id="element-1666">
        {/* Header with status + owner controls */}
        <div className="flex-shrink-0 px-6 py-4 bg-white border-b border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-id="element-1667">
          <div className="flex items-center gap-3" data-id="element-1668">
            <div className="w-11 h-11 rounded-full bg-gradient-to-br from-primary-indigo to-ai-purple flex items-center justify-center text-white font-bold" data-id="element-1669">
              {lead.name.charAt(0)}
            </div>
            <div data-id="element-1670">
              <h2 className="text-lg font-display font-bold text-dark-navy" data-id="element-1671">
                {lead.name}
              </h2>
              <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${bucket.bg} ${bucket.color}`} data-id="element-1672">
                {bucket.label} · {lead.intentScore}%
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2" data-id="element-1673">
            <Dropdown label="Status" value={status} options={PIPELINE_STAGES as unknown as string[]} onChange={v => changeStatus(v as LeadStatus)} data-id="element-1674" />
            <Dropdown label="Owner" value={owner} options={OWNERS as unknown as string[]} onChange={changeOwner} data-id="element-1675" />
            <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-full transition-colors" data-id="element-1676">
              <XIcon className="w-5 h-5" data-id="element-1677" />
            </button>
          </div>
        </div>

        <div className="flex flex-1 overflow-hidden" data-id="element-1678">
          {/* Tab rail */}
          <div className="w-52 flex-shrink-0 border-r border-slate-100 bg-white p-3 overflow-y-auto hidden md:block" data-id="element-1679">
            <div className="space-y-1" data-id="element-1680">
              {tabs.map(t => <button key={t.id} onClick={() => setTab(t.id)} className={`w-full flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${tab === t.id ? 'bg-indigo-50 text-primary-indigo' : 'text-slate-600 hover:bg-slate-50'}`} data-id="element-1681">
                  <span className={tab === t.id ? 'text-primary-indigo' : 'text-slate-400'} data-id="element-1682">
                    {t.icon}
                  </span>
                  {t.id}
                </button>)}
            </div>
          </div>

          {/* Mobile tab select */}
          <div className="md:hidden absolute top-[88px] left-0 right-0 px-4 z-10" data-id="element-1683">
            <select value={tab} onChange={e => setTab(e.target.value as DetailTab)} className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm shadow-sm" data-id="element-1684">
              {tabs.map(t => <option key={t.id} value={t.id} data-id="element-1685">
                  {t.id}
                </option>)}
            </select>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6 md:pt-6 pt-16" data-id="element-1686">
            {tab === 'Overview' && <div className="space-y-6 max-w-3xl" data-id="element-1687">
                <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-soft" data-id="element-1688">
                  <div className="flex items-center justify-between mb-1" data-id="element-1689">
                    <span className="text-sm text-slate-500" data-id="element-1690">Intent Score</span>
                    <span className="font-bold text-dark-navy" data-id="element-1691">
                      {lead.intentScore}% · {bucket.label}
                    </span>
                  </div>
                  <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden" data-id="element-1692">
                    <div className={`h-full ${bucket.bar}`} style={{
                  width: `${lead.intentScore}%`
                }} data-id="element-1693" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4" data-id="element-1694">
                  <Info label="Looking For" value={lead.lookingFor} data-id="element-1695" />
                  <Info label="Budget" value={lead.budget} data-id="element-1696" />
                  <Info label="Timeline" value={lead.timeline} data-id="element-1697" />
                  <Info label="Location" value={lead.location} data-id="element-1698" />
                  <Info label="Matched Listing" value={lead.matchedItem} data-id="element-1699" />
                  <Info label="Status" value={status} data-id="element-1700" />
                </div>
                <NextActionCard lead={lead} data-id="element-1701" />
                <div data-id="element-1702">
                  <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3" data-id="element-1703">
                    Why Talab Matched This Lead
                  </h3>
                  <div className="bg-white rounded-2xl border border-slate-100 p-4 shadow-soft grid grid-cols-2 gap-2.5" data-id="element-1704">
                    {['Budget Match', 'Location Match', 'Category Match', 'Timeline Match', 'Preference Match'].map(m => <div key={m} className="flex items-center gap-2 text-sm text-dark-navy" data-id="element-1705">
                        <CheckCircle2Icon className="w-4 h-4 text-green-500 flex-shrink-0" data-id="element-1706" />{' '}
                        {m}
                      </div>)}
                  </div>
                </div>
              </div>}

            {tab === 'Conversation Timeline' && <div className="max-w-2xl" data-id="element-1707">
                <p className="text-sm text-slate-500 mb-6" data-id="element-1708">
                  How Talab qualified this lead.
                </p>
                <div className="space-y-1" data-id="element-1709">
                  <TimelineBubble role="customer" text="Find me a Prado." data-id="element-1710" />
                  <TimelineArrow data-id="element-1711" />
                  <TimelineBubble role="talab" text="What's your budget?" data-id="element-1712" />
                  <TimelineArrow data-id="element-1713" />
                  <TimelineBubble role="customer" text="AED 90,000" data-id="element-1714" />
                  <TimelineArrow data-id="element-1715" />
                  <TimelineBubble role="talab" text="Preferred mileage?" data-id="element-1716" />
                  <TimelineArrow data-id="element-1717" />
                  <TimelineBubble role="customer" text="Under 100,000 km" data-id="element-1718" />
                  <TimelineArrow data-id="element-1719" />
                  <TimelineBubble role="talab" text="Recommended 3 vehicles" data-id="element-1720" />
                  <TimelineArrow data-id="element-1721" />
                  <TimelineBubble role="customer" text="Can I see this vehicle?" data-id="element-1722" />
                  <TimelineArrow data-id="element-1723" />
                  <div className="flex items-center gap-2 mt-2 px-4 py-3 bg-green-50 border border-green-100 rounded-xl" data-id="element-1724">
                    <CheckCircle2Icon className="w-5 h-5 text-green-600" data-id="element-1725" />
                    <span className="font-bold text-green-700 text-sm" data-id="element-1726">
                      Lead Created
                    </span>
                  </div>
                </div>
              </div>}

            {tab === 'Customer Details' && <div className="max-w-2xl" data-id="element-1727">
                <ConsentBanner consent={lead.consent} data-id="element-1728" />
                {hasContact ? <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-soft grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4" data-id="element-1729">
                    <Info label="Name" value={lead.name} data-id="element-1730" />
                    <Info label="Phone" value={lead.phone ?? '—'} data-id="element-1731" />
                    <Info label="Email" value={lead.email ?? '—'} data-id="element-1732" />
                    <Info label="Preferred Language" value={lead.preferredLanguage ?? 'English'} data-id="element-1733" />
                    <Info label="Preferred Contact" value={lead.preferredContact ?? '—'} data-id="element-1734" />
                    <Info label="City" value={lead.location} data-id="element-1735" />
                    <Info label="Budget" value={lead.budget} data-id="element-1736" />
                    <Info label="Timeline" value={lead.timeline} data-id="element-1737" />
                  </div> : <div className="bg-white rounded-2xl border border-slate-100 p-8 text-center shadow-soft mt-4" data-id="element-1738">
                    <ShieldAlertIcon className="w-10 h-10 text-slate-300 mx-auto mb-3" data-id="element-1739" />
                    <h4 className="font-bold text-dark-navy mb-1" data-id="element-1740">
                      Contact not shared yet
                    </h4>
                    <p className="text-sm text-slate-500" data-id="element-1741">
                      Customer has not requested seller contact.
                    </p>
                  </div>}
              </div>}

            {tab === 'Matched Listings' && <div className="max-w-2xl space-y-3" data-id="element-1742">
                <div className="bg-white rounded-2xl border border-slate-100 p-4 shadow-soft flex items-center gap-3" data-id="element-1743">
                  <div className="w-16 h-16 rounded-xl bg-slate-100 overflow-hidden flex-shrink-0" data-id="element-1744">
                    <img src="https://images.unsplash.com/photo-1550133730-695473e544be?auto=format&fit=crop&q=80&w=120" alt={lead.matchedItem} className="w-full h-full object-cover" data-id="element-1745" />
                  </div>
                  <div className="flex-1" data-id="element-1746">
                    <div className="font-bold text-dark-navy text-sm" data-id="element-1747">
                      {lead.matchedItem}
                    </div>
                    <div className="text-xs text-slate-500" data-id="element-1748">
                      98% match · {lead.budget}
                    </div>
                  </div>
                  <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-bold rounded-full" data-id="element-1749">
                    Best match
                  </span>
                </div>
                <div className="bg-white rounded-2xl border border-slate-100 p-4 shadow-soft flex items-center gap-3" data-id="element-1750">
                  <div className="w-16 h-16 rounded-xl bg-slate-100 overflow-hidden flex-shrink-0" data-id="element-1751">
                    <img src="https://images.unsplash.com/photo-1533473359331-0135ef1b58bf?auto=format&fit=crop&q=80&w=120" alt="Alt" className="w-full h-full object-cover" data-id="element-1752" />
                  </div>
                  <div className="flex-1" data-id="element-1753">
                    <div className="font-bold text-dark-navy text-sm" data-id="element-1754">
                      Nissan Patrol 2020
                    </div>
                    <div className="text-xs text-slate-500" data-id="element-1755">81% match</div>
                  </div>
                </div>
              </div>}

            {tab === 'AI Recommendations' && <div className="max-w-2xl space-y-6" data-id="element-1756">
                <div className="bg-gradient-to-br from-indigo-900 to-purple-900 rounded-2xl p-5 text-white shadow-medium" data-id="element-1757">
                  <div className="flex items-center gap-2 mb-3" data-id="element-1758">
                    <SparklesIcon className="w-4 h-4" data-id="element-1759" />
                    <span className="font-bold text-sm" data-id="element-1760">AI Buyer Analysis</span>
                  </div>
                  <ul className="space-y-2 text-sm text-indigo-50" data-id="element-1761">
                    <li className="flex gap-2" data-id="element-1762">
                      <span className="text-electric-cyan" data-id="element-1763">•</span> High
                      probability buyer.
                    </li>
                    <li className="flex gap-2" data-id="element-1764">
                      <span className="text-electric-cyan" data-id="element-1765">•</span> Actively
                      comparing GCC vehicles.
                    </li>
                    <li className="flex gap-2" data-id="element-1766">
                      <span className="text-electric-cyan" data-id="element-1767">•</span> Likely
                      purchase timeline: 7 days.
                    </li>
                    <li className="flex gap-2" data-id="element-1768">
                      <span className="text-electric-cyan" data-id="element-1769">•</span> Budget
                      flexibility: ~AED 5,000.
                    </li>
                  </ul>
                </div>
                <div className="space-y-3" data-id="element-1770">
                  {['Offer financing options', 'Do not suggest imported vehicles', 'Highlight service history', 'Offer delivery'].map(rec => <RecommendationRow key={rec} text={rec} data-id="element-1771" />)}
                </div>
                <div className="flex items-center justify-between bg-green-50 border border-green-100 rounded-xl px-4 py-3" data-id="element-1772">
                  <span className="text-sm font-medium text-green-800" data-id="element-1773">
                    Probability of conversion
                  </span>
                  <span className="text-lg font-bold text-green-700" data-id="element-1774">87%</span>
                </div>
              </div>}

            {tab === 'Activity Log' && <div className="max-w-2xl" data-id="element-1775">
                <div className="relative border-l-2 border-slate-100 ml-2 space-y-6" data-id="element-1776">
                  {activity.map((a, i) => <div key={i} className="relative pl-6" data-id="element-1777">
                      <div className="absolute -left-[7px] top-1.5 w-3 h-3 rounded-full bg-primary-indigo border-2 border-white" data-id="element-1778" />
                      <div className="text-sm font-medium text-dark-navy" data-id="element-1779">
                        {a.text}
                      </div>
                      <div className="text-xs text-slate-400" data-id="element-1780">{a.time}</div>
                    </div>)}
                </div>
              </div>}

            {tab === 'Notes' && <div className="max-w-2xl" data-id="element-1781">
                <h3 className="text-sm font-bold text-dark-navy mb-2" data-id="element-1782">
                  Internal Team Notes
                </h3>
                <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={6} className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-indigo/20 text-sm resize-none shadow-soft" placeholder="Add a note for your team…" data-id="element-1783" />
                <button className="mt-3 px-5 py-2.5 bg-primary-indigo text-white text-sm font-medium rounded-xl hover:bg-indigo-700 transition-colors" data-id="element-1784">
                  Save Note
                </button>
              </div>}
          </div>
        </div>
      </motion.div>
    </div>;
}
function Dropdown({
  label,
  value,
  options,
  onChange
}: {
  label: string;
  value: string;
  options: string[];
  onChange: (v: string) => void;
}) {
  return <div className="relative" data-id="element-1785">
      <select value={value} onChange={e => onChange(e.target.value)} aria-label={label} className="appearance-none pl-3 pr-8 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm font-medium text-dark-navy focus:ring-2 focus:ring-primary-indigo/20 cursor-pointer" data-id="element-1786">
        {options.map(o => <option key={o} value={o} data-id="element-1787">
            {label}: {o}
          </option>)}
      </select>
      <ChevronDownIcon className="w-4 h-4 text-slate-400 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" data-id="element-1788" />
    </div>;
}
function Info({
  label,
  value
}: {
  label: string;
  value: string;
}) {
  return <div className="bg-white rounded-xl border border-slate-100 px-4 py-3 shadow-soft" data-id="element-1789">
      <div className="text-xs text-slate-400 mb-0.5" data-id="element-1790">{label}</div>
      <div className="text-sm font-bold text-dark-navy" data-id="element-1791">{value}</div>
    </div>;
}
function NextActionCard({
  lead
}: {
  lead: Lead;
}) {
  return <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-soft" data-id="element-1792">
      <div className="flex items-center justify-between mb-3" data-id="element-1793">
        <h3 className="text-sm font-bold text-dark-navy" data-id="element-1794">Next Action</h3>
        <span className="text-xs text-amber-600 font-medium flex items-center gap-1" data-id="element-1795">
          <ClockIcon className="w-3.5 h-3.5" data-id="element-1796" /> Due{' '}
          {lead.nextActionDue ?? 'Tomorrow'}
        </span>
      </div>
      <select defaultValue={lead.nextAction ?? 'Call customer'} className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm mb-3 focus:ring-2 focus:ring-primary-indigo/20" data-id="element-1797">
        {['Call customer', 'Send WhatsApp', 'Follow up tomorrow', 'Send quotation', 'Book viewing', 'Offer delivery', 'Close as lost'].map(a => <option key={a} data-id="element-1798">{a}</option>)}
      </select>
      <label className="flex items-center gap-2 text-sm text-slate-600" data-id="element-1799">
        <input type="checkbox" defaultChecked className="rounded text-primary-indigo focus:ring-primary-indigo" data-id="element-1800" />{' '}
        Set reminder
      </label>
    </div>;
}
function RecommendationRow({
  text
}: {
  text: string;
}) {
  const [state, setState] = useState<'open' | 'done' | 'ignored'>('open');
  return <div className={`flex items-center justify-between gap-3 bg-white border border-slate-100 rounded-xl px-4 py-3 shadow-soft ${state !== 'open' ? 'opacity-60' : ''}`} data-id="element-1801">
      <span className="flex items-center gap-2 text-sm text-slate-700" data-id="element-1802">
        <SparklesIcon className="w-4 h-4 text-ai-purple flex-shrink-0" data-id="element-1803" />
        {text}
        {state === 'done' && ' · Done'}
        {state === 'ignored' && ' · Ignored'}
      </span>
      {state === 'open' && <div className="flex items-center gap-1 flex-shrink-0" data-id="element-1804">
          <button className="px-2.5 py-1 bg-primary-indigo text-white text-xs font-medium rounded-md hover:bg-indigo-700" data-id="element-1805">
            Create Message
          </button>
          <button onClick={() => setState('done')} className="p-1.5 text-green-600 hover:bg-green-50 rounded-md" title="Mark Done" data-id="element-1806">
            <CheckIcon className="w-4 h-4" data-id="element-1807" />
          </button>
          <button onClick={() => setState('ignored')} className="p-1.5 text-slate-400 hover:bg-slate-100 rounded-md" title="Ignore" data-id="element-1808">
            <XIcon className="w-4 h-4" data-id="element-1809" />
          </button>
        </div>}
    </div>;
}
function ConsentBanner({
  consent
}: {
  consent?: Lead['consent'];
}) {
  if (consent === 'shared') return <div className="flex items-center gap-2 px-4 py-3 bg-green-50 border border-green-100 rounded-xl text-sm text-green-800" data-id="element-1810">
        <ShieldCheckIcon className="w-4 h-4" data-id="element-1811" /> Contact shared by customer
      </div>;
  if (consent === 'requested') return <div className="flex items-center gap-2 px-4 py-3 bg-amber-50 border border-amber-100 rounded-xl text-sm text-amber-800" data-id="element-1812">
        <ShieldAlertIcon className="w-4 h-4" data-id="element-1813" /> Contact requested
      </div>;
  return <div className="flex items-center gap-2 px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-600" data-id="element-1814">
      <ShieldAlertIcon className="w-4 h-4" data-id="element-1815" /> Contact not shared yet
    </div>;
}
function TimelineBubble({
  role,
  text
}: {
  role: 'customer' | 'talab';
  text: string;
}) {
  const isTalab = role === 'talab';
  return <div className={`flex gap-2 ${isTalab ? '' : 'flex-row-reverse'}`} data-id="element-1816">
      <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${isTalab ? 'bg-gradient-to-br from-primary-indigo to-ai-purple text-white' : 'bg-slate-200 text-slate-600'}`} data-id="element-1817">
        {isTalab ? <BotIcon className="w-3.5 h-3.5" data-id="element-1818" /> : <UserIcon className="w-3.5 h-3.5" data-id="element-1819" />}
      </div>
      <div className={`max-w-[80%] px-3.5 py-2 rounded-2xl text-sm ${isTalab ? 'bg-white border border-slate-200 text-slate-800 rounded-tl-sm shadow-soft' : 'bg-primary-indigo text-white rounded-tr-sm'}`} data-id="element-1820">
        {text}
      </div>
    </div>;
}
function TimelineArrow() {
  return <div className="flex justify-center py-0.5" data-id="element-1821">
      <ArrowDownIcon className="w-4 h-4 text-slate-300" data-id="element-1822" />
    </div>;
}